import { providers } from '@indigohive/cogfy-providers'
import { RefreshCcw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateConnectionButton } from '../../../CreateConnectionButton'
import { CustomSelectionMenuItem } from '../../../CustomSelectionMenuItem'
import { Menu } from '../../../Menu'
import { OverlayContent } from '../../../OverlayContent'
import { FormInputProps } from '../../types'
import { Container } from '../../../Container'
import { CreateConnectionDialog } from '../../../../pages/WorkspaceConnectionsPage/components/CreateConnectionDialog'
import { USE_CUSTOM_DIALOG } from '../../../../pages'

export function ConnectionFormInput (props: FormInputProps) {
  const providerType = props.schema.type
  const provider = providers.filter(provider => provider.name === providerType)[0]
  const [selectConnectionEl, setSelectConnectionEl] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const [lastCreatedConnection, setLastCreatedConnection] = useState<string | null>(null)

  useEffect(() => {
    if (lastCreatedConnection) {
      handleChange({
        target: { value: `connection:${lastCreatedConnection}` }
      })
    }
  }, [props.connections])

  const [menuWidth, setMenuWidth] = useState(0)
  useEffect(() => {
    if (selectConnectionEl) {
      setMenuWidth(selectConnectionEl.getBoundingClientRect().width)
    }
  }, [selectConnectionEl])

  const connectionOptions = useMemo(
    () => (props.connections ?? [])
      .filter(connection =>
        providerType === 'connection' ||
        connection.provider === providerType
      )
      .map(connection => ({
        label: `Connection: ${connection.name}${connection.default ? ' (default)' : ''}`,
        value: `connection:${connection.id}`
      })),
    [props.connections]
  )
  const fieldOptions = useMemo(
    () => (props.fields ?? [])
      .filter(field => field.type === 'connection')
      .map(field => ({
        label: `Field: ${field.name}`,
        value: `field:${field.id}`
      })),
    [props.fields, providerType]
  )

  const handleChange = (event: { target: { value: string } }) => {
    if (event.target.value.startsWith('field:')) {
      const fieldId = event.target.value.split(':')[1]
      const field = props.fields?.find(field => field.id === fieldId)

      if (field) {
        props.onChange({
          type: 'fields',
          fields: { value: [{ id: field.id }] }
        })
      } else {
        props.onChange(undefined)
      }
    } else if (event.target.value.startsWith('connection:')) {
      const connectionId = event.target.value.split(':')[1]
      const connection = props.connections?.find(connection => connection.id === connectionId)

      if (connection) {
        props.onChange({
          type: 'connection',
          connection: {
            value: {
              id: connection.id,
              provider: connection.provider
            }
          }
        })
      } else {
        props.onChange(undefined)
      }
    } else if (event.target.value === 'cogfy-credits') {
      props.onChange({
        type: 'connection',
        connection: {
          value: {
            cogfyCredits: true
          }
        }
      })
    } else {
      props.onChange(undefined)
    }
  }

  const value = props.value?.type === 'connection'
    ? props.value.connection.value.cogfyCredits ? 'cogfy-credits' : `connection:${props.value.connection.value.id}`
    : props.value?.type === 'fields' && props.value.fields.value.length > 0
      ? `field:${props.value.fields.value[0].id}`
      : ''

  const canUseCogfyCredits = Boolean(props.eligibleCogfyCreditsProviders?.find(p => p.provider === providerType))
  const hasDefaultConnection = Boolean(props.connections?.find(connection => connection.default && connection.provider === providerType))

  const options = [
    { label: 'Select a connection', value: '' },
    ...(canUseCogfyCredits ? [{ label: `Cogfy credits${hasDefaultConnection ? '' : ' (default)'}`, value: 'cogfy-credits' }] : []),
    ...connectionOptions,
    ...fieldOptions
  ]

  return (
    <div>
      <div
        className="select w-full rounded-lg border-black/20 btn-sm font-normal justify-between z-0"
        onClick={event => {
          setSelectConnectionEl(event.currentTarget)
        }}
      >
        <div className='flex items-center justify-between w-full mr-5 disabled'>
          {!value && 'Select a connection'}
          {value && (
            options.find(option => option.value === value)?.label
          )}
          <button
            className='btn btn-circle btn-xs btn-ghost z-10 flex items-center justify-center'
            onClick={event => {
              event.stopPropagation()
              if (props.getConnections) {
                props.getConnections.refetch()
              }
            }}
            disabled={props.getConnections?.isRefetching}
          >
            {props.getConnections?.isRefetching
              ? (
                <span className="loading loading-spinner loading-xs"></span>
                )
              : (
                <RefreshCcw size={14}/>
                )
            }
          </button>
        </div>
      </div>
      <OverlayContent
        open={Boolean(selectConnectionEl)}
        anchorEl={selectConnectionEl}
        onClose={() => setSelectConnectionEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Menu className="max-h-[400px]" style={{ width: `${menuWidth}px` }} maxWidth={false}>
          <CreateConnectionButton
            provider={provider}
            className='text-primary justify-start w-full'
            onCreateConnectionClick={USE_CUSTOM_DIALOG.has(provider.name)
              ? undefined
              : () => setOpen(true)
            }
            onSuccess={props.getConnections?.refetch}
          >
            {t('common:Create connection')}
          </CreateConnectionButton>
          {options.map(option => {
            return (
              <CustomSelectionMenuItem
                key={option.value}
                onChange={handleChange}
                setSelectEl={setSelectConnectionEl}
                value={option.value}
                label={option.label}
              />
            )
          })}
        </Menu>
      </OverlayContent>
      <Container>
        <CreateConnectionDialog
          provider={provider}
          open={open}
          onClose={() => setOpen(false)}
          onSuccess={(createdConnectionId: string) => {
            setOpen(false)
            props.getConnections?.refetch()
            setLastCreatedConnection(createdConnectionId)
          }}
        />
      </Container>
    </div>
  )
}
